/** Small formatters the Reports sections share (split out of `Reports.tsx` when the screen became
 * a list of filings and a filing's own tabs; moved verbatim, so the reasoning in each comment
 * still holds). */

import type { ReportLine } from "../api/queries";
import { intensity, quantity } from "../lib/format";

export function cls(...names: (string | undefined | false)[]): string {
  return names.filter(Boolean).join(" ");
}

/** A figure, at the precision its unit is reported to.
 *
 * **The formatting is delegated, and that is the repair rather than the style.** This function used
 * to call `toLocaleString(undefined, …)`, and `undefined` means *the reader’s browser* — so the
 * same SECR total rendered `1,629,851.4` on the register and `1.629.851,4` to somebody whose
 * browser is set to German, on the screen whose entire job is to preview what will be filed. A
 * reader comparing the two reads the second as one thousand six hundred. It also made
 * `format.ts`’s claim to be *the only place a quantity becomes display text* false, which is the
 * worse half: a false sentence in a docstring is what the next reader reasons from.
 *
 * What stays here is the one thing this screen knows and the formatter does not — kWh is reported
 * whole and a tonnage to a tenth.
 */
export function amount(value: string, unit: string | null): string {
  // An intensity ratio's unit is `tCO2e per <denominator>` (`reporting.py`), and a ratio is a small
  // number: a tenth prints 0.1 for 0.116. It takes the display rule's three significant figures.
  if (unit !== null && unit.startsWith("tCO2e per ")) return intensity(value);
  return quantity(value, unit === "kWh" ? 0 : 1);
}

/** A movement, with its direction on its face — ACC-10.
 *
 * **Signed, because an unsigned difference reads as a magnitude.** `difference-delta` on the
 * reconciliation screen already carries one and the gate asserts it there; this is the same figure
 * on the other screen, so it is the same rule. A negative string already carries its `-`, so only
 * the positive case needs a mark — and zero gets neither, because a movement of nothing has no
 * direction and `+0.0` would invent one.
 *
 * ASCII `+` and `-`, not a typographic minus: the sign is what a test asserts on, and a character
 * a keyboard cannot type is a character a later edit replaces.
 */
export function signedTonnes(value: string, places = 1): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  const shown = quantity(value, places);
  // The sign follows what is **printed**: `0.04` prints `0.0` and a `+0.0` would invent a direction.
  return parsed > 0 && /[1-9]/.test(shown) ? `+${shown}` : shown;
}

/** The receipt ids a line carries, tolerant of a server that predates the field.
 *
 * `ReportLineOut.receipt_ids` is a default-`[]` field the API gained after the image on the estate
 * was built, so a response from an older API has neither it nor `receipt_ids_truncated`. Treating
 * an absent list as empty keeps the line honest (it then says it lists no receipts) instead of
 * throwing on `.length` in front of a customer. */
export function receiptIdsOf(line: ReportLine): { ids: string[]; truncated: boolean } {
  const wire = line as Partial<Pick<ReportLine, "receipt_ids" | "receipt_ids_truncated">>;
  return { ids: wire.receipt_ids ?? [], truncated: wire.receipt_ids_truncated === true };
}

/** What the factors behind a line's figure leave out (Q-17). The same tolerance as `receiptIdsOf`: a
 * response from an API built before the field has none, and none is an empty list, not a throw. */
export function factorLimitationsOf(line: ReportLine): ReportLine["factor_limitations"] {
  const wire = line as Partial<Pick<ReportLine, "factor_limitations">>;
  return wire.factor_limitations ?? [];
}
